import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
// Shared POS catalog (src/data/posCatalog.js) — also rendered in Festival HQ pricing.
import { POS_PRODUCT_CATALOG as DEFAULT_PRODUCT_CATALOG, POS_TABS as DEFAULT_TABS, centsToUsd } from '../data/posCatalog';
import './PosProductsPage.css';

const READER_STORAGE_KEY = 'posProducts.selectedReaderId';
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;
const FIXED_TAX_PERCENT = 7.75;

const buildInitialQuantities = (products = DEFAULT_PRODUCT_CATALOG) => {
  const out = {};
  products.forEach((p) => {
    out[p.id] = 0;
  });
  return out;
};

const sortReaders = (rows = []) => {
  const rank = (r) => {
    const s = String(r?.status || '').toLowerCase();
    if (s === 'online') return 0;
    if (s === 'offline') return 2;
    return 1;
  };
  return [...rows].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    const sim = Number(Boolean(a?.is_simulated)) - Number(Boolean(b?.is_simulated));
    if (sim !== 0) return sim;
    return String(a?.label || '').localeCompare(String(b?.label || ''));
  });
};

const PosProductsLoginGate = ({ onSuccess }) => {
  const [staffList, setStaffList] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.getPosStaffList().then((resp) => {
      if (cancelled) return;
      if (resp?.success) setStaffList(resp.data || []);
    });
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedEmail) return;
    setLoading(true);
    setError('');
    try {
      const resp = await api.posStaffLogin(selectedEmail);
      if (resp?.success) {
        const user = api.getUser();
        if (!user?.is_staff && !user?.is_superuser) {
          api.clearTokens();
          setError('Staff access required.');
          return;
        }
        const access = sessionStorage.getItem('accessToken');
        const refresh = sessionStorage.getItem('refreshToken');
        if (access) localStorage.setItem('pos_accessToken', access);
        if (refresh) localStorage.setItem('pos_refreshToken', refresh);
        onSuccess();
      } else {
        setError(resp?.error?.message || 'Login failed');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pp-gate">
      <div className="pp-gate-shell">
        <div className="pp-gate-side">
          <div className="pp-gate-brand">OC MENA FESTIVAL</div>
          <h1>POS Products</h1>
          <p>Simple terminal pairing and manual pricing.</p>
        </div>

        <form className="pp-gate-card" onSubmit={submit}>
          <h2>Staff sign in</h2>
          <p>Select your name to continue.</p>
          <label className="pp-field">
            <span>Staff member</span>
            <select
              className="pp-input"
              value={selectedEmail}
              onChange={(e) => setSelectedEmail(e.target.value)}
              required
              autoFocus
            >
              <option value="">Choose staff…</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.email}>{s.full_name || s.email}</option>
              ))}
            </select>
          </label>
          {error && <div className="pp-error-banner">{error}</div>}
          <button type="submit" className="pp-btn pp-btn-primary pp-gate-submit" disabled={loading || !selectedEmail}>
            {loading ? 'Signing in…' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

const ReaderSettingsModal = ({
  open,
  onClose,
  readers,
  selectedReaderId,
  onSelect,
  onReadersChanged,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pairCode, setPairCode] = useState('');
  const [pairLabel, setPairLabel] = useState('Reader 12');
  const backdropMouseDownRef = useRef(false);

  const orderedReaders = useMemo(() => sortReaders(readers), [readers]);
  const currentReader = useMemo(
    () => orderedReaders.find((r) => r.id === selectedReaderId) || null,
    [orderedReaders, selectedReaderId],
  );

  if (!open) return null;

  const refresh = async () => {
    setBusy(true);
    setError('');
    try {
      const resp = await api.posGetReaders(true);
      if (resp?.success) onReadersChanged(sortReaders(resp.data.readers || []));
    } catch (err) {
      setError(err.message || 'Failed to refresh readers');
    } finally {
      setBusy(false);
    }
  };

  const pair = async () => {
    setBusy(true);
    setError('');
    try {
      await api.posEnsureLocation();
      const resp = await api.posRegisterReader(pairCode.trim(), pairLabel.trim() || 'Reader');
      if (!resp?.success) {
        setError(resp?.error?.message || 'Failed to pair');
        return;
      }
      const nextRows = sortReaders([resp.data.reader, ...readers.filter((r) => r.id !== resp.data.reader.id)]);
      onReadersChanged(nextRows);
      onSelect(resp.data.reader.id);
      setPairCode('');
    } catch (err) {
      setError(err.message || 'Failed to pair reader');
    } finally {
      setBusy(false);
    }
  };

  const removeReader = async (reader) => {
    if (!reader?.id) return;
    if (!window.confirm(`Remove terminal "${reader.label}"?`)) return;
    setBusy(true);
    setError('');
    try {
      await api.posDeleteReader(reader.id);
      const nextRows = sortReaders(readers.filter((r) => r.id !== reader.id));
      onReadersChanged(nextRows);
      if (selectedReaderId === reader.id) {
        const fallback = nextRows.find((r) => !r.is_simulated) || nextRows[0] || null;
        onSelect(fallback?.id || null);
      }
    } catch (err) {
      setError(err.message || 'Failed to remove reader');
    } finally {
      setBusy(false);
    }
  };

  const renameReader = async (reader) => {
    if (!reader?.id) return;
    const nextLabel = window.prompt('Rename terminal label', reader.label || 'Reader');
    if (nextLabel == null) return;
    const clean = nextLabel.trim();
    if (!clean) {
      setError('Reader label is required');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const resp = await api.posUpdateReader(reader.id, { label: clean });
      if (!resp?.success) {
        setError(resp?.error?.message || 'Failed to rename reader');
        return;
      }
      const updated = resp.data.reader;
      const nextRows = sortReaders([updated, ...readers.filter((r) => r.id !== updated.id)]);
      onReadersChanged(nextRows);
    } catch (err) {
      setError(err.message || 'Failed to rename reader');
    } finally {
      setBusy(false);
    }
  };

  const onBackdropMouseDown = (e) => {
    backdropMouseDownRef.current = e.target === e.currentTarget;
  };

  const onBackdropMouseUp = (e) => {
    const isBackdrop = e.target === e.currentTarget;
    if (backdropMouseDownRef.current && isBackdrop) onClose();
    backdropMouseDownRef.current = false;
  };

  return (
    <div
      className="pp-modal-backdrop"
      onMouseDown={onBackdropMouseDown}
      onMouseUp={onBackdropMouseUp}
    >
      <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal-head">
          <div>
            <h2>Terminal settings</h2>
            <div className="pp-modal-current">
              Current: {currentReader ? currentReader.label : 'No terminal selected'}
            </div>
          </div>
          <button className="pp-icon-btn" onClick={onClose}>×</button>
        </div>

        <div className="pp-modal-body">
          <div className="pp-reader-list">
            {orderedReaders.length === 0 && (
              <div className="pp-empty">No readers synced yet.</div>
            )}
            {orderedReaders.map((r) => (
              <div
                key={r.id}
                className={`pp-reader-row ${selectedReaderId === r.id ? 'is-selected' : ''}`}
              >
                <button className="pp-reader-main" onClick={() => onSelect(r.id)}>
                  <div className="pp-reader-label">{r.label}</div>
                  <div className="pp-reader-meta">
                    {(r.status || 'unknown').toUpperCase()} · {r.device_type}{r.is_simulated ? ' · SIMULATOR' : ''}
                    {r.assigned_cashier_name ? ` · ${r.assigned_cashier_name}` : ''}
                    {r.assigned_cashier_email && !r.assigned_cashier_name ? ` · ${r.assigned_cashier_email}` : ''}
                  </div>
                </button>
                <div className="pp-reader-actions">
                  <button
                    className="pp-btn pp-btn-secondary"
                    onClick={() => onSelect(r.id)}
                    disabled={selectedReaderId === r.id}
                  >
                    {selectedReaderId === r.id ? 'Current' : 'Use'}
                  </button>
                  <button className="pp-btn pp-btn-secondary" onClick={() => renameReader(r)} disabled={busy}>Rename</button>
                  <button className="pp-btn pp-btn-ghost" onClick={() => removeReader(r)} disabled={busy}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          <div className="pp-settings-actions">
            <button className="pp-btn pp-btn-secondary" onClick={refresh} disabled={busy}>
              {busy ? 'Working…' : 'Refresh readers'}
            </button>
          </div>

          <div className="pp-pair-box">
            <h3>Pair S710</h3>
            <div className="pp-pair-grid">
              <input
                className="pp-input"
                type="text"
                value={pairLabel}
                onChange={(e) => setPairLabel(e.target.value)}
                placeholder="Reader label"
              />
              <input
                className="pp-input"
                type="text"
                value={pairCode}
                onChange={(e) => setPairCode(e.target.value.toLowerCase())}
                placeholder="pairing-code-words"
                autoCapitalize="off"
              />
              <button className="pp-btn pp-btn-primary" onClick={pair} disabled={busy || !pairCode.trim()}>
                Pair
              </button>
            </div>
            <p>On S710: Settings → Generate pairing code. Remove old simulator readers if you want a fresh list.</p>
          </div>

          {error && <div className="pp-error-banner">{error}</div>}
        </div>
      </div>
    </div>
  );
};

const ChargeOverlay = ({ sale, onCancel, onDone, onSimulatorTap, isSimulator, cancelLoading }) => {
  if (!sale) return null;
  const status = sale.status;
  const headline = {
    PENDING: 'Starting…',
    PROCESSING: 'Present card on reader',
    SUCCEEDED: 'Payment successful',
    FAILED: sale.failure_message || 'Payment failed',
    CANCELLED: 'Payment cancelled',
  }[status] || 'Working…';

  const terminal = ['SUCCEEDED', 'FAILED', 'CANCELLED'].includes(status);

  return (
    <div className="pp-overlay">
      <div className="pp-overlay-card">
        <div className="pp-overlay-amount">{centsToUsd(sale.total_cents || 0)}</div>
        <div className="pp-overlay-headline">{headline}</div>
        {sale.order_number && <div className="pp-overlay-sub">Order {sale.order_number}</div>}

        {status === 'SUCCEEDED' && (sale.items?.length > 0 || sale.pos_line_items?.length > 0) && (
          <div className="pp-overlay-items">
            <div className="pp-overlay-items-title">Purchased</div>
            {(sale.pos_line_items?.length > 0 ? sale.pos_line_items : sale.items).map((item, idx) => (
              <div key={idx} className="pp-overlay-item-row">
                <span>{item.name || item.product_name || 'Item'}</span>
                <strong>
                  {item.quantity || item.qty || 1} × {centsToUsd(item.unit_price_cents || item.price_cents || 0)}
                </strong>
              </div>
            ))}
          </div>
        )}

        {!terminal && isSimulator && (
          <div className="pp-sim-actions">
            <button className="pp-btn pp-btn-secondary" onClick={() => onSimulatorTap('visa')}>Sim Visa</button>
            <button className="pp-btn pp-btn-secondary" onClick={() => onSimulatorTap('mastercard')}>Sim MC</button>
            <button className="pp-btn pp-btn-secondary" onClick={() => onSimulatorTap('refused')}>Sim Decline</button>
          </div>
        )}

        <div className="pp-overlay-actions">
          {!terminal && (
            <button className="pp-btn pp-btn-secondary" onClick={onCancel} disabled={cancelLoading}>
              {cancelLoading ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
          {terminal && (
            <button className="pp-btn pp-btn-primary" onClick={onDone}>New payment</button>
          )}
        </div>
      </div>
    </div>
  );
};

const SalesHistoryModal = ({ open, onClose }) => {
  const [sales, setSales] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [totalSalesCents, setTotalSalesCents] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const backdropMouseDownRef = useRef(false);

  const load = useCallback((nextOffset) => {
    setBusy(true);
    setErr('');
    api.posGetRecentSales({ mine: true, limit, offset: nextOffset })
      .then((resp) => {
        if (resp?.success) {
          setSales(resp.data.sales || []);
          setCount(resp.data.count || 0);
          setOffset(resp.data.offset || nextOffset);
          setTotalSalesCents(resp.data.total_sales_cents || 0);
        } else {
          setErr(resp?.error?.message || 'Failed to load sales');
        }
      })
      .catch((e) => setErr(e.message || 'Failed to load sales'))
      .finally(() => setBusy(false));
  }, [limit]);

  useEffect(() => {
    if (!open) return;
    load(0);
  }, [open, load]);

  const onBackdropMouseDown = (e) => {
    backdropMouseDownRef.current = e.target === e.currentTarget;
  };
  const onBackdropMouseUp = (e) => {
    if (backdropMouseDownRef.current && e.target === e.currentTarget) onClose();
    backdropMouseDownRef.current = false;
  };

  const hasPrev = offset > 0;
  const hasNext = offset + limit < count;

  const cardInfo = (s) => {
    if (s.card_brand && s.card_last4) return `${s.card_brand} ·••• ${s.card_last4}`;
    if (s.card_brand) return s.card_brand;
    if (s.card_last4) return `•••• ${s.card_last4}`;
    return '';
  };

  if (!open) return null;

  return (
    <div className="pp-modal-backdrop" onMouseDown={onBackdropMouseDown} onMouseUp={onBackdropMouseUp}>
      <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal-head">
          <div>
            <h2>My sales</h2>
            <div className="pp-modal-current">
              {count} transaction(s) · Total {centsToUsd(totalSalesCents)}
            </div>
          </div>
          <button className="pp-icon-btn" onClick={onClose}>×</button>
        </div>
        <div className="pp-modal-body">
          {busy && <div className="pp-empty">Loading…</div>}
          {err && <div className="pp-error-banner">{err}</div>}
          {!busy && sales.length === 0 && <div className="pp-empty">No sales yet.</div>}
          {!busy && sales.length > 0 && (
            <>
              <div className="pp-sales-list">
                {sales.map((s) => (
                  <button
                    key={s.id}
                    className="pp-sales-row pp-sales-clickable"
                    onClick={() => setExpandedId((prev) => (prev === s.id ? null : s.id))}
                  >
                    <div className="pp-sales-header">
                      <strong>{centsToUsd(s.total_cents || 0)}</strong>
                      <span className={`pp-sales-badge ${(s.status || '').toLowerCase()}`}>{s.status}</span>
                    </div>
                    <div className="pp-sales-meta">
                      {s.order_number || '—'} · {s.reader_label || '—'} · {new Date(s.created_at).toLocaleString()}
                      {cardInfo(s) ? ` · ${cardInfo(s)}` : ''}
                    </div>
                    {s.items?.length > 0 && (
                      <div className="pp-sales-item-names">
                        {s.items.map((it, i) => (it.name || it.product_name)).filter(Boolean).join(', ')}
                      </div>
                    )}
                    {expandedId === s.id && s.items?.length > 0 && (
                      <div className="pp-sales-items">
                        {s.items.map((it, i) => (
                          <div key={i} className="pp-sales-item">
                            {it.quantity || 1}× {it.name || it.product_name || 'Item'} — {centsToUsd(it.total_cents || (it.unit_price_cents || it.price_cents || 0) * (it.quantity || 1))}
                          </div>
                        ))}
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="pp-sales-paging">
                <button className="pp-btn pp-btn-secondary" onClick={() => load(offset - limit)} disabled={!hasPrev || busy}>
                  Previous
                </button>
                <span className="pp-paging-info">{offset + 1}–{Math.min(offset + limit, count)} of {count}</span>
                <button className="pp-btn pp-btn-secondary" onClick={() => load(offset + limit)} disabled={!hasNext || busy}>
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PosProductsPage = () => {
  const navigate = useNavigate();

  const [authed, setAuthed] = useState(() => {
    // Restore POS tokens from localStorage if session was cleared
    const sessionAccess = sessionStorage.getItem('accessToken');
    const sessionRefresh = sessionStorage.getItem('refreshToken');
    if (!sessionAccess || !sessionRefresh) {
      const posAccess = localStorage.getItem('pos_accessToken');
      const posRefresh = localStorage.getItem('pos_refreshToken');
      if (posAccess && posRefresh) {
        api.setTokens(posAccess, posRefresh);
      }
    }
    const user = api.getUser();
    return api.isAuthenticated() && (user?.is_staff || user?.is_superuser);
  });
  const [me, setMe] = useState(() => api.getUser());

  const [readers, setReaders] = useState([]);
  const [selectedReaderId, setSelectedReaderId] = useState(() => localStorage.getItem(READER_STORAGE_KEY) || null);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [productCatalog, setProductCatalog] = useState(DEFAULT_PRODUCT_CATALOG);
  const [tabs, setTabs] = useState(DEFAULT_TABS);
  const [activeTab, setActiveTab] = useState('ALL');
  const [quantities, setQuantities] = useState(() => buildInitialQuantities());
  const [manualCents, setManualCents] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSale, setActiveSale] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [readerOffModal, setReaderOffModal] = useState(false);

  const pollRef = useRef(null);
  const pollStartedAtRef = useRef(null);

  const selectedReader = useMemo(
    () => readers.find((r) => r.id === selectedReaderId) || null,
    [readers, selectedReaderId],
  );
  const staffUsername = useMemo(() => {
    const email = String(me?.email || '').trim().toLowerCase();
    if (!email) return '';
    return email.split('@')[0] || '';
  }, [me]);

  const isAdmin = useMemo(() => !!(me?.is_superuser || me?.role === 'ADMIN'), [me]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (selectedReaderId) localStorage.setItem(READER_STORAGE_KEY, selectedReaderId);
    else localStorage.removeItem(READER_STORAGE_KEY);
  }, [selectedReaderId]);

  const loadReaders = useCallback(async (sync = false) => {
    const resp = await api.posGetReaders(sync);
    if (resp?.success) {
      const nextRows = sortReaders(resp.data.readers || []);
      setReaders(nextRows);
      if (!selectedReaderId && nextRows.length > 0) {
        const userId = me?.id;
        const assigned = userId ? nextRows.find((r) => r.assigned_cashier_id === userId) : null;
        const preferred = assigned || nextRows.find((r) => !r.is_simulated) || nextRows[0];
        setSelectedReaderId(preferred.id);
      }
    }
  }, [selectedReaderId, me]);

  useEffect(() => {
    if (!authed) return;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const [, catalogResp] = await Promise.all([
          loadReaders(false),
          api.posGetManualProductCatalog(),
        ]);
        if (catalogResp?.success && catalogResp.data?.configured) {
          const nextProducts = (catalogResp.data.products || []).filter((product) => product.is_active !== false);
          const nextTabs = catalogResp.data.categories || [];
          if (nextProducts.length) {
            setProductCatalog(nextProducts);
            setQuantities((prev) => Object.fromEntries(nextProducts.map((product) => [product.id, Number(prev[product.id] || 0)])));
          }
          if (nextTabs.length) setTabs(nextTabs);
        }
      } catch (err) {
        setError(err.message || 'Failed to load terminals');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [authed, loadReaders]);

  // Force each staff member to their assigned reader (ignore stale localStorage from other users)
  useEffect(() => {
    if (!authed || !me?.id || readers.length === 0) return;
    const assigned = readers.find((r) => r.assigned_cashier_id === me.id);
    if (assigned && selectedReaderId !== assigned.id) {
      setSelectedReaderId(assigned.id);
    }
  }, [authed, me, readers, selectedReaderId]);

  const computedLines = useMemo(() => {
    if (activeTab === 'MANUAL') {
      if (manualCents <= 0) return [];
      return [{
        id: 'manual',
        name: 'Manual entry',
        quantity: 1,
        unit_price_cents: manualCents,
      }];
    }
    return productCatalog
      .map((product) => ({
        id: product.id,
        name: product.name,
        quantity: Math.max(0, Number(quantities[product.id] || 0)),
        unit_price_cents: product.unit_price_cents,
        tax_exempt: Boolean(product.tax_exempt),
      }))
      .filter((line) => line.quantity > 0);
  }, [quantities, activeTab, manualCents, productCatalog]);

  const subtotalCents = useMemo(
    () => computedLines.reduce((sum, l) => sum + (l.quantity * l.unit_price_cents), 0),
    [computedLines],
  );

  const taxableSubtotalCents = useMemo(
    () => computedLines.reduce((sum, l) => sum + (l.tax_exempt ? 0 : (l.quantity * l.unit_price_cents)), 0),
    [computedLines],
  );

  const taxCents = useMemo(
    () => Math.round(taxableSubtotalCents * (FIXED_TAX_PERCENT / 100)),
    [taxableSubtotalCents],
  );
  const totalCents = subtotalCents + taxCents;

  const canCharge = !!selectedReader && computedLines.length > 0 && totalCents > 0;

  const incrementQty = (productId, delta) => {
    setQuantities((prev) => {
      const current = Number(prev[productId] || 0);
      const next = Math.max(0, Math.min(99, current + delta));
      return { ...prev, [productId]: next };
    });
  };

  const handleManualDigit = (digit) => {
    setManualCents((prev) => {
      const next = prev * 10 + digit;
      return next > 999999 ? prev : next;
    });
  };

  const handleManualBackspace = () => {
    setManualCents((prev) => Math.floor(prev / 10));
  };

  const handleManualClear = () => setManualCents(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollStartedAtRef.current = null;
  }, []);

  const startPolling = useCallback((orderId) => {
    stopPolling();
    pollStartedAtRef.current = Date.now();
    pollRef.current = setInterval(async () => {
      try {
        const resp = await api.posGetChargeStatus(orderId);
        if (resp?.success) {
          const sale = resp.data.sale;
          setActiveSale(sale);
          if (['SUCCEEDED', 'FAILED', 'CANCELLED'].includes(sale.status)) stopPolling();
        }
      } catch (_) {
        // Keep polling on transient failure.
      }
      if (pollStartedAtRef.current && Date.now() - pollStartedAtRef.current > POLL_TIMEOUT_MS) {
        stopPolling();
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const charge = async () => {
    if (!selectedReader) {
      setError('Select a terminal first.');
      setShowSettings(true);
      return;
    }
    if (!computedLines.length) {
      setError(activeTab === 'MANUAL' ? 'Enter an amount first.' : 'Select at least one product.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const resp = await api.posCreateManualCharge(
        selectedReader.id,
        computedLines,
        {},
        { taxPercent: FIXED_TAX_PERCENT, mode: 'staff' },
      );

      if (!resp?.success) {
        const msg = resp?.error?.message || 'Failed to create charge';
        const code = resp?.error?.code || '';
        if (code === 'reader_error' || /timeout.*reader|reader.*timeout/i.test(msg)) {
          setReaderOffModal(true);
        } else {
          setError(msg);
        }
        return;
      }

      setActiveSale(resp.data.sale);
      startPolling(resp.data.sale.order_id);
    } catch (err) {
      const code = err.response?.data?.error?.code || '';
      const msg = err.message || 'Failed to create charge';
      if (code === 'reader_error' || /timeout.*reader|reader.*timeout/i.test(msg)) {
        setReaderOffModal(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelCharge = async () => {
    if (!activeSale || cancelLoading) return;
    const orderId = activeSale.order_id;
    stopPolling();
    setActiveSale(null);
    setCancelLoading(true);
    try {
      await api.posCancelCharge(orderId);
    } catch (err) {
      setError(err.message || 'Cancel failed');
    } finally {
      setCancelLoading(false);
    }
  };

  const simulatorTap = async (cardType) => {
    if (!selectedReader?.is_simulated) return;
    try {
      await api.posSimulatorTap(selectedReader.id, cardType);
    } catch (err) {
      setError(err.message || 'Simulator failed');
    }
  };

  const newPayment = () => {
    const didSucceed = activeSale?.status === 'SUCCEEDED';
    stopPolling();
    setActiveSale(null);
    if (!didSucceed) return;
    setQuantities(buildInitialQuantities(productCatalog));
    setManualCents(0);
  };

  const signOut = () => {
    api.clearTokens();
    localStorage.removeItem('pos_accessToken');
    localStorage.removeItem('pos_refreshToken');
    setAuthed(false);
    setMe(null);
    navigate('/pos-products', { replace: true });
  };

  if (!authed) {
    return <PosProductsLoginGate onSuccess={() => { setAuthed(true); setMe(api.getUser()); }} />;
  }

  return (
    <div className="pp-root">
      <header className="pp-topbar">
        {isAdmin && (
          <div>
            <div className="pp-brand">POS Products</div>
            <div className="pp-sub">Pair terminal · type products · charge card{me?.email ? ` · ${me.email}` : ''}</div>
          </div>
        )}

        <div className="pp-top-actions">
          <button className="pp-btn pp-btn-ghost" onClick={signOut}>Sign out</button>
          <button className="pp-btn pp-btn-secondary" onClick={() => setShowHistory(true)}>My sales</button>
          {isAdmin && (
            <>
              <button className="pp-reader-chip" onClick={() => setShowSettings(true)}>
                {selectedReader ? selectedReader.label : 'Select terminal'}
              </button>
              <button className="pp-icon-btn" onClick={() => setShowSettings(true)} title="Terminal settings">⚙</button>
            </>
          )}
          {!isAdmin && staffUsername && (
            <span className="pp-staff-badge">{staffUsername}</span>
          )}
        </div>
      </header>

      {isAdmin && (
        <div className="pp-current-device-strip">
          <div className="pp-current-device-label">Current staff</div>
          <div className="pp-current-device-name">{staffUsername || me?.full_name || me?.email || 'Unknown staff'}</div>
          <div className="pp-current-device-meta">
            {selectedReader
              ? `Terminal: ${selectedReader.label} · ${(selectedReader.status || 'unknown').toUpperCase()} · ${selectedReader.device_type}${selectedReader.is_simulated ? ' · SIMULATOR' : ''}`
              : 'Open terminal settings to choose a reader.'}
          </div>
        </div>
      )}

      {error && <div className="pp-error-banner pp-layout-error">{error}</div>}

      <main className="pp-layout">
        <section className="pp-card pp-amount-card">
          <div className="pp-amount-label">Amount to charge</div>
          <div className="pp-amount-value">{centsToUsd(totalCents)}</div>
          <div className="pp-amount-sub">USD</div>
        </section>

        <section className="pp-card">
          {activeTab !== 'MANUAL' && <h2>Products</h2>}
          {activeTab !== 'MANUAL' && <div className="pp-empty">Tap + / − only. No typing needed.</div>}
          {loading && activeTab !== 'MANUAL' && <div className="pp-empty">Loading terminals…</div>}

          <div className="pp-tabs">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`pp-tab ${activeTab === tab ? 'is-active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {activeTab === 'MANUAL' ? (
            <div className="pp-numpad">
              {[1,2,3,4,5,6,7,8,9].map((n) => (
                <button key={n} className="pp-numpad-btn" onClick={() => handleManualDigit(n)}>{n}</button>
              ))}
              <button className="pp-numpad-btn pp-numpad-clear" onClick={handleManualClear}>C</button>
              <button className="pp-numpad-btn" onClick={() => handleManualDigit(0)}>0</button>
              <button className="pp-numpad-btn" onClick={handleManualBackspace}>⌫</button>
            </div>
          ) : (
            <div className="pp-product-grid">
              {productCatalog.filter((p) => p.categories.includes(activeTab)).sort((a, b) => a.name.localeCompare(b.name)).map((product) => {
                const qty = Number(quantities[product.id] || 0);
                const lineTotal = qty * product.unit_price_cents;
                return (
                  <div key={product.id} className={`pp-product-card ${qty > 0 ? 'is-active' : ''}`}>
                    <div className="pp-product-name">{product.name}</div>
                    <div className="pp-product-price">{centsToUsd(product.unit_price_cents)}</div>
                    <div className="pp-qty-controls">
                      <button className="pp-qty-btn" onClick={() => incrementQty(product.id, -1)}>-</button>
                      <input
                        className="pp-qty-display"
                        type="text"
                        value={qty}
                        readOnly
                      />
                      <button className="pp-qty-btn" onClick={() => incrementQty(product.id, 1)}>+</button>
                    </div>
                    <div className="pp-product-line-total">Line total: {centsToUsd(lineTotal)}</div>
                  </div>
                );
              })}
            </div>
          )}

          {computedLines.length > 0 && (
            <div className="pp-selected-list">
              {computedLines.map((line) => (
                <div key={line.id} className="pp-total-row">
                  <span>{line.name} × {line.quantity}</span>
                  <strong>{centsToUsd(line.quantity * line.unit_price_cents)}</strong>
                </div>
              ))}
            </div>
          )}

          <div className="pp-sticky-footer">
            <div className="pp-totals">
              <div className="pp-total-row"><span>Subtotal</span><strong>{centsToUsd(subtotalCents)}</strong></div>
              <div className="pp-total-row"><span>Tax ({FIXED_TAX_PERCENT.toFixed(2)}%)</span><strong>{centsToUsd(taxCents)}</strong></div>
              <div className="pp-total-row pp-total-grand"><span>Total</span><strong>{centsToUsd(totalCents)}</strong></div>
            </div>

            <button className="pp-btn pp-btn-primary pp-charge-btn" onClick={charge} disabled={!canCharge || loading}>
              {loading ? 'Processing…' : (selectedReader ? `Charge on ${selectedReader.label}` : 'Select terminal')}
            </button>
            {computedLines.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setQuantities(buildInitialQuantities(productCatalog));
                  setManualCents(0);
                }}
                style={{ marginTop: 8, background: 'none', border: 'none', color: '#EF4444', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', width: '100%', textAlign: 'center' }}
              >
                Clear {activeTab === 'MANUAL' ? 'amount' : 'cart'}
              </button>
            )}
          </div>
        </section>
      </main>

      {!isAdmin && (
        <div className="pp-current-device-strip pp-current-device-strip-bottom">
          <div className="pp-current-device-label">Current staff</div>
          <div className="pp-current-device-name">{staffUsername || me?.full_name || me?.email || 'Unknown staff'}</div>
          <div className="pp-current-device-meta">
            {selectedReader
              ? `Terminal: ${selectedReader.label} · ${(selectedReader.status || 'unknown').toUpperCase()} · ${selectedReader.device_type}${selectedReader.is_simulated ? ' · SIMULATOR' : ''}`
              : 'Open terminal settings to choose a reader.'}
          </div>
        </div>
      )}

      <ReaderSettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        readers={readers}
        selectedReaderId={selectedReaderId}
        onSelect={setSelectedReaderId}
        onReadersChanged={setReaders}
      />

      <ChargeOverlay
        sale={activeSale}
        onCancel={cancelCharge}
        onDone={newPayment}
        onSimulatorTap={simulatorTap}
        isSimulator={!!selectedReader?.is_simulated}
        cancelLoading={cancelLoading}
      />

      {readerOffModal && (
        <div className="pp-overlay">
          <div className="pp-overlay-card">
            <div className="pp-overlay-headline" style={{ marginBottom: 8 }}>Terminal not responding</div>
            <div className="pp-overlay-sub" style={{ marginBottom: 18 }}>
              The device is off or unreachable. Please turn it on and try again.
            </div>
            <div className="pp-overlay-actions">
              <button className="pp-btn pp-btn-primary" onClick={() => setReaderOffModal(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      <SalesHistoryModal open={showHistory} onClose={() => setShowHistory(false)} />
    </div>
  );
};

export default PosProductsPage;
